import { useCallback, useMemo } from 'react';
import { isEmpty, map } from 'lodash';
import { Link } from 'react-router-dom';
import {
  Stack, Link as MuiLink, Grid, Typography,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useDispatch, useSelector } from '@client/hooks/redux.hooks';
import { getCurrentShow, getCurrentShowProfiles, getProductsForShow } from '@client/selectors/entities/shows';
import { Checkbox, Form } from '@client/components/forms';
import { startCheckoutFreeTickets, startCheckoutSession, startCreateStripeUnifiedSale } from '@client/slices/features/stripe.reducer';
import { Button } from '@client/components/common/Button';
import { openSignupModal } from '@client/components/Main/Signup';
import { showSnackbar } from '@client/listeners/lib/snackbar';
import { baseUrl } from '@sda/utils/url';
import { Modal } from '@client/components/globals/Modal';
import { SignupIntents } from '@sda/enums';
import { getSessionUserState } from '@client/selectors/helpers/login';
import { joinBy } from '@sda/utils/array';
import { hasFreeProduct } from '@sda/utils/meta';
import { hasPaidProductSelected } from '../common/utils';
import { OrderTable } from './OrderTable';
import { openGuestReservationModal } from './GuestReservation';
import { TicketListItem } from './TicketListItem';
import type { FormikProps } from 'formik';
import type { TicketFormPayload } from '../interfaces';

const useStyles = makeStyles()((theme) => ({
  form: {
    [theme.breakpoints.up('md')]: {
      marginBottom: theme.spacing(3 * -1),
      marginLeft: theme.spacing(3 * -1),
      width: `calc(100% + ${theme.spacing(3)})`,
    },
  },
  ticketListContainer: {
    [theme.breakpoints.up('md')]: {
      borderRightColor: theme.palette.divider,
      borderRightStyle: 'solid',
      borderRightWidth: 1,
      overflowY: 'auto',
    },
  },
  ticketListItem: {
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(3),

    [theme.breakpoints.up('md')]: {
      paddingRight: theme.spacing(3),
      paddingLeft: theme.spacing(3),
    },

    '&:not(:first-of-type)': {
      borderTopColor: theme.palette.divider,
      borderTopStyle: 'solid',
      borderTopWidth: 1,
    },
  },
  orderTableContainer: {
    [theme.breakpoints.down('md')]: {
      paddingBottom: theme.spacing(3),
    },

    [theme.breakpoints.up('md')]: {
      paddingTop: theme.spacing(3),
      paddingLeft: theme.spacing(3),
      position: 'relative',
    },
  },
  orderTableStack: {
    [theme.breakpoints.up('md')]: {
      position: 'sticky',
      top: theme.spacing(3),
      paddingBottom: theme.spacing(3),
    },
  },
}));

export const TicketModalForm: React.FC<FormikProps<TicketFormPayload>> = ({
  setSubmitting, values, setTouched, errors,
  touched,
}) => {
  const { classes } = useStyles();
  const dispatch = useDispatch();

  const show = useSelector(getCurrentShow);
  const products = useSelector(getProductsForShow);
  const userState = useSelector(getSessionUserState);
  const profiles = useSelector(getCurrentShowProfiles);

  const buttonScope = userState ? 'start-checkout-session' : 'reserve-show-tickets';

  const isFreePurchase = !hasPaidProductSelected(values.prices) && hasFreeProduct(products);

  const handleRegularCheckoutSuccess = useCallback((options?: { shareContact: boolean, checkout: boolean }) => {
    if (options?.checkout) {
      showSnackbar('Please sign up to purchase a ticket.', 'warning');

      const onSuccess = () => {
        if (isFreePurchase) {
          dispatch(startCreateStripeUnifiedSale({
            shareContact: options.shareContact,
            showId: show.id,
          }));
        } else {
          dispatch(startCheckoutSession({
            shareContact: options.shareContact,
            successUrl: baseUrl('/checkout/signup'),
          }));
        }
      };

      openSignupModal({
        onSuccess,
        acceptsTos: true,
        successScope: 'start-checkout-session',
        intent: SignupIntents.AUDIENCE,
      });
    } else {
      // This should only be dispatched by reserving a ticket to a free show
      Modal.hide();

      showSnackbar((
        <>
          Your ticket(s) were successfully reserved.
          <MuiLink
            component={Link}
            to="/mytickets"
            display="block"
          >
            View My Tickets
          </MuiLink>
        </>
      ));
    }
  }, [dispatch, show.id, isFreePurchase]);

  const handleGuestSubmit = useCallback(() => {
    if (isFreePurchase) {
      openGuestReservationModal({
        onSuccess: (guestReservationValues) => dispatch(startCheckoutFreeTickets({
          shareContact: values.shareContact,
          ...guestReservationValues,
        })),
      });
    } else {
      dispatch(startCheckoutSession({ shareContact: values.shareContact, scope: 'start-guest-checkout-session', successUrl: baseUrl('/checkout/signup') }));
    }
  }, [dispatch, isFreePurchase, values]);

  const handleGuestCheckoutSuccess = useCallback((options?: { shareContact: boolean, checkout: boolean }) => {
    if (options?.checkout) {
      dispatch(startCheckoutSession({
        shareContact: options.shareContact,
        successUrl: baseUrl('/checkout/signup'),
      }));
    } else {
      // This should only be dispatched by reserving a ticket to a free show
      Modal.hide();

      showSnackbar((
        <>
          Your ticket(s) were successfully reserved.
          <MuiLink
            component={Link}
            to="/mytickets"
            display="block"
          >
            View My Tickets
          </MuiLink>
        </>
      ));
    }
  }, [dispatch]);

  const shareLabel = useMemo(() => `Share my email with ${joinBy(profiles, 'name', ' and ') || 'the show creator'}`, [profiles]);

  return (
    <Form className={classes.form}>
      <Grid
        container
        alignItems="stretch"
      >
        <Grid
          item
          xs={12}
          md={6}
          className={classes.ticketListContainer}
        >
          {map(products, (product, index) => (
            <div
              className={classes.ticketListItem}
              key={product.id}
            >
              <TicketListItem
                index={index}
                {...product}
              />
            </div>
          ))}
        </Grid>

        <Grid
          item
          xs={12}
          md={6}
          className={classes.orderTableContainer}
        >
          <Stack
            spacing={3}
            className={classes.orderTableStack}
          >
            <OrderTable />

            <Checkbox
              name="shareContact"
              labelNoWrap={false}
            >
              {shareLabel}
            </Checkbox>

            {!userState && (
              <Checkbox
                name="acceptsTos"
                labelNoWrap={false}
                required
              >
                I accept Side Door's
                {' '}

                <MuiLink
                  href={baseUrl('terms-of-service')}
                  id="sda-terms-of-service-link"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Terms of Use
                </MuiLink>
              </Checkbox>
            )}

            <Stack spacing={1.5}>
              {!isEmpty(errors.prices) && !isEmpty(touched.prices) && (
                <Typography
                  variant="body1"
                  color="error"
                  align="center"
                >
                  Please select a ticket to continue
                </Typography>
              )}

              <Button
                type="submit"
                id="sda-start-checkout-tickets-button"
                scope={buttonScope}
                aria-label="buy tickets"
                variant="contained"
                size="large"
                color="success"
                onError={() => setSubmitting(false)}
                onSuccess={handleRegularCheckoutSuccess}
              >
                {!userState ? 'Sign Up' : (
                  <>
                    {isFreePurchase ? 'Reserve Tickets' : 'Buy Tickets'}
                  </>
                )}
              </Button>

              {!userState && (
              <Button
                type="button"
                id="sda-start-guest-checkout-tickets-button"
                scope="start-guest-checkout-session"
                aria-label="buy tickets as guest"
                variant="outlined"
                size="large"
                color="success"
                onError={() => setSubmitting(false)}
                onSuccess={handleGuestCheckoutSuccess}
                onClick={() => {
                  void setTouched({ prices: [{ id: true, quantity: true, ticketPrice: true }], acceptsTos: true }, true).then((result) => {
                    if (isEmpty(result)) {
                      handleGuestSubmit();
                    }
                  });
                }}
              >
                Guest Checkout
              </Button>
              )}
            </Stack>
          </Stack>
        </Grid>
      </Grid>
    </Form>
  );
};
